import { SetStateAction, useEffect, useMemo, useState } from 'react';
import './Customer.scss';
import Header from '../../../components/Header/Header';
import ProfileTab from './ProfileTab/ProfileTab';
import WalletsTab from './WalletsTab/WalletsTab';
import InterestTab from './InterestTab/InterestTab';
import CardsAndBanksTab from './CardsAndBanksTab/CardsAndBanksTab';
import { Loader } from '@piggytechng/component-library';
import useCustomers from '../../../hooks/useCustomers';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DeviceTab from './DeviceTab/DeviceTab';
import { IoCopyOutline } from 'react-icons/io5';
import { copyToClipboard } from '../../../helpers/utils';
import QuickActions from '../../../components/QuickActions';
import { AccountStateSummary, ManagedAccountSummary, WalletTypes } from '../../../types';
import Piggypoints from './Piggypoints/Piggypoints';
import WalletActionTab from './WalletAction/WalletAction';
import SummaryStatementTab from './SummaryStatementTab/SummaryStatementTab';
import FlagsAndActionsTab from './FlagAndActionsTab/FlagsAndActionsTab';
import useRolePermissions from '../../../hooks/useRolePermissions';
import PasswordAndOTP from './PasswordAndOTP/PasswordAndOTP';
import ProductsTab from './ProductsTab/ProductsTab';
import ManagedAccountsTab from './ManagedAccountsTab/ManagedAccountsTab';

const Customer = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const stored = localStorage.getItem('activeTab');
    return stored === 'kids-accounts' ? 'managed-accounts' : stored || 'profile';
  });
  const [activeTabWallet, setActiveTabWallet] = useState<WalletTypes>(
    (localStorage.getItem('activeTabWallet') as WalletTypes) || 'housemoney',
  );
  const [searchParams] = useSearchParams();
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const navigate = useNavigate();
  const { restrictTabViewByRole } = useRolePermissions({
    checkCustomer: true,
  });

  const currentViewedWallet = useMemo(() => {
    return searchParams.get('wallet') ?? '';
  }, [searchParams, activeTabWallet]);

  const currentBankAndCardTab = useMemo(() => {
    return searchParams.get('active') ?? '';
  }, [searchParams]);

  const { singleCustomer, isLoading } = useCustomers();
  const params = useParams();
  const id = params.id as string;

  // A Kids/Joint account is a customer row too. When this profile is one, show it as the account it is and keep the
  // person-level tabs (credentials, devices, cards, flags) off it; core refuses those actions on managed rows anyway.
  const managed: ManagedAccountSummary | null = singleCustomer?.managed ?? null;
  const headerName = managed
    ? managed.displayName
    : `${singleCustomer?.firstName ?? ''} ${singleCustomer?.lastName ?? ''}`.trim();
  const initials =
    headerName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || '?';
  // Set by an admin from Flags & Actions > Account Status; the profile payload carries the summary
  const accountState: AccountStateSummary | null = singleCustomer?.accountState ?? null;
  const isDeactivated = accountState?.state === 'deactivated';
  const hiddenForManaged = ['managed-accounts', 'cardsAndsbanks', 'devices', 'piggypoints', 'flags', 'password'];
  const showTab = (tab: string) => !managed || !hiddenForManaged.includes(tab);

  useEffect(() => {
    if (managed && hiddenForManaged.includes(activeTab)) setActiveTab('wallets');
  }, [managed, activeTab]);

  useEffect(() => {
    localStorage.setItem('activeTabWallet', activeTabWallet);
  }, [activeTabWallet]);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    let actionType = '';
    activeTab === 'flags' && (actionType = 'flagAndActions');
    activeTab === 'cardsAndsbanks' && (actionType = 'bankAndCards');
    activeTab === 'devices' && (actionType = 'manageDevices');
    activeTab === 'piggypoints' && (actionType = 'piggypoints');

    actionType.length &&
      restrictTabViewByRole(actionType, 'view', () => setActiveTab(localStorage.getItem('activeTab') || 'profile'));
  }, [activeTab]);

  const activeAction = useMemo(() => {
    if (activeTab !== 'wallets' && activeTab !== 'cardsAndsbanks') {
      return activeTab;
    }

    if (activeTab === 'wallets' && currentViewedWallet === 'target') {
      return searchParams.get('status') === 'active' ? 'target' : '';
    }

    if (activeTab === 'cardsAndsbanks') {
      switch (currentBankAndCardTab) {
        case 'withdrawals':
          return 'withdrawals';
        default:
          return '';
      }
    }

    return activeTabWallet;
  }, [currentViewedWallet, currentBankAndCardTab, searchParams, activeTabWallet, activeTab]);

  useEffect(() => {
    localStorage.setItem('activeCustomer', id);
  }, []);

  return (
    <>
      <Header
        name="Customer"
        backButton
        backPath="/customers"
        backCallBack={() => {
          setActiveTab('profile');
          setActiveTabWallet('housemoney');
          localStorage.setItem('activeCustomer', '');
        }}
      />

      {isLoading ? (
        <div className="page-loader">
          <Loader color="#0F60D6" />
        </div>
      ) : (
        <>
          {singleCustomer && (
            <div className="customer-page">
              <div className="customer-page__card">
                <div className="flex--2 customer-page-header">
                  {singleCustomer?.bvn?.data?.fileName ? (
                    <button
                      type="button"
                      className="customer-page-avi"
                      onClick={() => setPreviewImageOpen(true)}
                      title="View full image"
                    >
                      <img
                        src={singleCustomer?.bvn?.data?.fileName}
                        alt={`${singleCustomer?.firstName ?? ''} ${singleCustomer?.lastName ?? ''}`.trim()}
                      />
                    </button>
                  ) : (
                    <div className="customer-page-initials" aria-hidden="true" title={headerName}>
                      {initials}
                    </div>
                  )}
                  <div className="customer-page-header--details flex--4">
                    <h1 className="customer-page-header--name">
                      <span>{headerName}</span>
                      {managed ? (
                        <>
                          <span
                            className="customer-status-badge customer-status-badge--managed"
                            title="A managed account: no login or contact details of its own"
                          >
                            <span className="customer-status-badge__dot" aria-hidden />
                            {managed.kind === 'kids'
                              ? 'Kids account'
                              : managed.kind === 'joint'
                                ? 'Joint account'
                                : 'Managed account'}
                          </span>
                          <span className={`customer-status-badge customer-status-badge--${managed.status}`}>
                            <span className="customer-status-badge__dot" aria-hidden />
                            {managed.status}
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            className={`customer-status-badge ${
                              singleCustomer?.flagged
                                ? 'customer-status-badge--flagged'
                                : 'customer-status-badge--active'
                            }`}
                            title={singleCustomer?.flagged ? 'This user is flagged' : 'This user is active'}
                          >
                            <span className="customer-status-badge__dot" aria-hidden />
                            {singleCustomer?.flagged ? 'Flagged' : 'Active'}
                          </span>
                          {isDeactivated && (
                            <span
                              className="customer-status-badge customer-status-badge--deactivated"
                              title={`Deactivated${accountState?.adminName ? ` by ${accountState.adminName}` : ''}${
                                accountState?.reason ? `: ${accountState.reason}` : ''
                              }`}
                            >
                              <span className="customer-status-badge__dot" aria-hidden />
                              Deactivated
                            </span>
                          )}
                        </>
                      )}
                    </h1>
                    <div className="flex--2 cont">
                      <div className="flex--2">
                        <p>
                          {managed ? 'Account ID' : 'User ID'}: {id}
                        </p>
                        <IoCopyOutline
                          size={18}
                          className="pointer"
                          onClick={() => copyToClipboard(managed ? 'Account ID' : 'User ID', id)}
                        />
                      </div>

                      {managed ? (
                        <>
                          <div className="flex--2">
                            <p>
                              Managed by:{' '}
                              {managed.owner ? (
                                <Link to={`/customer/${managed.owner.userId}`} title="Open the owner's profile">
                                  {managed.owner.name}
                                </Link>
                              ) : (
                                'no owner on record'
                              )}
                            </p>
                          </div>
                          <div className="flex--2">
                            <p>Members: {managed.memberCount}</p>
                          </div>
                        </>
                      ) : (
                        <div className="flex--2">
                          <p>Email: {singleCustomer.email}</p>

                          <IoCopyOutline
                            size={18}
                            className="pointer"
                            onClick={() => copyToClipboard('Email', singleCustomer.email)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex--3">
                  <div className="tabs-container">
                    <div
                      className={`tab-buttons ${activeTab === 'profile' && 'active-tab'}`}
                      onClick={() => {
                        setActiveTab('profile');
                      }}
                    >
                      Profile Details
                    </div>
                    <div
                      className={`tab-buttons ${activeTab === 'wallets' && 'active-tab'}`}
                      onClick={() => setActiveTab('wallets')}
                    >
                      Wallets History
                    </div>
                    {showTab('managed-accounts') && (
                      <div
                        className={`tab-buttons ${activeTab === 'managed-accounts' && 'active-tab'}`}
                        onClick={() => setActiveTab('managed-accounts')}
                      >
                        Managed Accounts
                      </div>
                    )}

                    <div
                      className={`tab-buttons ${activeTab === 'wallet-action' && 'active-tab'}`}
                      onClick={() => setActiveTab('wallet-action')}
                    >
                      Wallet
                    </div>

                    <div
                      className={`tab-buttons ${activeTab === 'summary-statement' && 'active-tab'}`}
                      onClick={() => setActiveTab('summary-statement')}
                    >
                      Summary Statement
                    </div>

                    <div
                      className={`tab-buttons ${activeTab === 'products' && 'active-tab'}`}
                      onClick={() => setActiveTab('products')}
                    >
                      Products
                    </div>
                    <div
                      className={`tab-buttons ${activeTab === 'interest' && 'active-tab'}`}
                      onClick={() => setActiveTab('interest')}
                    >
                      Interest
                    </div>
                    {showTab('cardsAndsbanks') && (
                      <div
                        className={`tab-buttons ${activeTab === 'cardsAndsbanks' && 'active-tab'}`}
                        onClick={() => setActiveTab('cardsAndsbanks')}
                      >
                        Banks and Cards
                      </div>
                    )}
                    {showTab('devices') && (
                      <div
                        className={`tab-buttons ${activeTab === 'devices' && 'active-tab'}`}
                        onClick={() => setActiveTab('devices')}
                      >
                        Manage Devices
                      </div>
                    )}
                    {showTab('piggypoints') && (
                      <div
                        className={`tab-buttons ${activeTab === 'piggypoints' && 'active-tab'}`}
                        onClick={() => setActiveTab('piggypoints')}
                      >
                        Piggypoints
                      </div>
                    )}

                    {showTab('flags') && (
                      <div
                        className={`tab-buttons ${activeTab === 'flags' && 'active-tab'}`}
                        onClick={() => setActiveTab('flags')}
                      >
                        Flags & Actions
                      </div>
                    )}
                    {showTab('password') && (
                      <div
                        className={`tab-buttons ${activeTab === 'password' && 'active-tab'}`}
                        onClick={() => setActiveTab('password')}
                      >
                        Password/OTP
                      </div>
                    )}

                    <div
                      className={`tab-buttons ${activeTab === 'withdrawals' && 'active-tab'}`}
                      onClick={() => setActiveTab('withdrawals')}
                      style={{ pointerEvents: 'none' }}
                    >
                      Withdrawals
                      <span className="label withdraw">Coming Soon</span>
                    </div>
                  </div>

                  {<QuickActions activeTabWallet={activeAction} />}
                </div>

                <div className="tab-data">
                  {activeTab === 'profile' && <ProfileTab userData={singleCustomer} />}
                  {activeTab === 'managed-accounts' && <ManagedAccountsTab customerId={id} />}
                  {activeTab === 'wallets' && (
                    <WalletsTab
                      userId={id}
                      activeTabWallet={activeTabWallet}
                      setActiveTabWallet={(val: SetStateAction<WalletTypes>) => setActiveTabWallet(val)}
                      setActiveTab={setActiveTab}
                    />
                  )}
                  {activeTab === 'cardsAndsbanks' && <CardsAndBanksTab />}
                  {activeTab === 'interest' && <InterestTab />}
                  {activeTab === 'devices' && <DeviceTab userId={id} />}
                  {activeTab === 'piggypoints' && <Piggypoints userId={id} />}
                  {activeTab === 'wallet-action' && <WalletActionTab userId={id} />}
                  {activeTab === 'summary-statement' && <SummaryStatementTab />}
                  {activeTab === 'flags' && <FlagsAndActionsTab userId={id} />}
                  {activeTab === 'products' && (
                    <ProductsTab
                      activeSubTab={searchParams.get('product') || 'investify'}
                      setActiveSubTab={(val: string) => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('product', val);
                        navigate(`?${newParams.toString()}`, { replace: true });
                      }}
                    />
                  )}
                  {activeTab === 'password' && <PasswordAndOTP />}
                  {/* {activeTab === 'debit' && <DeviceTab userId={id} />} */}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {previewImageOpen && singleCustomer?.bvn?.data?.fileName && (
        <div
          className="image-preview-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImageOpen(false)}
        >
          <button
            type="button"
            className="image-preview-close"
            onClick={() => setPreviewImageOpen(false)}
            aria-label="Close image preview"
          >
            &times;
          </button>
          <img
            className="image-preview-image"
            src={singleCustomer?.bvn?.data?.fileName}
            alt={`${singleCustomer?.firstName ?? ''} ${singleCustomer?.lastName ?? ''}`.trim()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default Customer;
